import React, { Suspense, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import './styles/examination.css';
import { applyAccessibilityPrefs } from './pages/ExamSettingsPage';

import ExamLoginPage from './pages/ExamLoginPage';
import ExamDashboard from './pages/ExamDashboard';
import ExamProjectsPage from './pages/ExamProjectsPage';
import ExamProjectWorkspace from './pages/ExamProjectWorkspace';
import ExamSettingsPage from './pages/ExamSettingsPage';
import InstitutionQBankPage from './pages/InstitutionQBankPage';
import EvaluationsQueuePage from './pages/EvaluationsQueuePage';
import SystemAnalyticsPage from './pages/SystemAnalyticsPage';
import FacultyDashboardPage from './pages/FacultyDashboardPage';

/* ---- Workspace panels (lazy-loaded) ------------------------------------ */
const SourcesPanel       = React.lazy(() => import('./panels/SourcesPanel'));
const MappingPanel       = React.lazy(() => import('./panels/MappingPanel'));
const CKBPanel           = React.lazy(() => import('./panels/CKBPanel'));
const BlueprintPanel     = React.lazy(() => import('./panels/BlueprintPanel'));
const QuestionsPanel     = React.lazy(() => import('./panels/QuestionsPanel'));
const QuestionSetsPanel  = React.lazy(() => import('./panels/QuestionSetsPanel'));
const EvaluationPanel    = React.lazy(() => import('./panels/EvaluationPanel'));
const AnalyticsPanel     = React.lazy(() => import('./panels/AnalyticsPanel'));
const QBankUploadPanel   = React.lazy(() => import('./panels/QBankUploadPanel'));
const QBankMappingPanel  = React.lazy(() => import('./panels/QBankMappingPanel'));

function PanelLoader() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
      <div className="exam-spinner" style={{ width: 28, height: 28, borderWidth: 3 }} />
    </div>
  );
}

function LazyPanel({ component: Component }) {
  return (
    <Suspense fallback={<PanelLoader />}>
      <Component />
    </Suspense>
  );
}

/* ---- Auth guard --------------------------------------------------------- */
// Roles that have full ExamOS admin access
const EXAM_ADMIN_ROLES = new Set(['tenant_admin', 'coe', 'system_admin']);

function ExamProtectedRoute({ children, adminOnly = false }) {
  const token = localStorage.getItem('examToken') || localStorage.getItem('token');
  const role  = localStorage.getItem('examUserRole') || '';

  if (!token) return <Navigate to="/examination/login" replace />;

  // Admin-only pages redirect faculty/non-admin users to the faculty dashboard
  if (adminOnly && !EXAM_ADMIN_ROLES.has(role)) {
    return <Navigate to="/examination/faculty" replace />;
  }

  return children;
}

/* ---- Root ---------------------------------------------------------------- */
export default function ExaminationApp() {
  // Apply saved accessibility preferences on mount
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('examAccessibility') || '{}');
      if (Object.keys(saved).length) applyAccessibilityPrefs(saved);
    } catch { /* ignore */ }
  }, []);

  return (
    <Routes>
      {/* Public */}
      <Route path="login" element={<ExamLoginPage />} />

      {/* Root redirect */}
      <Route index element={<Navigate to="dashboard" replace />} />

      {/* Protected: Dashboard — admin only */}
      <Route
        path="dashboard"
        element={
          <ExamProtectedRoute adminOnly>
            <ExamDashboard />
          </ExamProtectedRoute>
        }
      />

      {/* Protected: Projects list — admin only */}
      <Route
        path="projects"
        element={
          <ExamProtectedRoute adminOnly>
            <ExamProjectsPage />
          </ExamProtectedRoute>
        }
      />

      {/* Protected: Project workspace — admin only */}
      <Route
        path="projects/:projectId"
        element={
          <ExamProtectedRoute adminOnly>
            <ExamProjectWorkspace />
          </ExamProtectedRoute>
        }
      >
        {/* Default sub-route */}
        <Route index element={<Navigate to="sources" replace />} />
        <Route path="sources"       element={<LazyPanel component={SourcesPanel} />} />
        <Route path="mapping"       element={<LazyPanel component={MappingPanel} />} />
        <Route path="ckb"           element={<LazyPanel component={CKBPanel} />} />
        <Route path="blueprint"     element={<LazyPanel component={BlueprintPanel} />} />
        <Route path="questions"     element={<LazyPanel component={QuestionsPanel} />} />
        <Route path="question-sets" element={<LazyPanel component={QuestionSetsPanel} />} />
        <Route path="evaluation"    element={<LazyPanel component={EvaluationPanel} />} />
        <Route path="analytics"     element={<LazyPanel component={AnalyticsPanel} />} />
        <Route path="qbank-upload"  element={<LazyPanel component={QBankUploadPanel} />} />
        <Route path="qbank-mapping" element={<LazyPanel component={QBankMappingPanel} />} />
      </Route>

      {/* Protected: Top-level sections — admin only */}
      <Route
        path="question-bank"
        element={
          <ExamProtectedRoute adminOnly>
            <InstitutionQBankPage />
          </ExamProtectedRoute>
        }
      />
      <Route
        path="evaluations"
        element={
          <ExamProtectedRoute adminOnly>
            <EvaluationsQueuePage />
          </ExamProtectedRoute>
        }
      />
      <Route
        path="analytics"
        element={
          <ExamProtectedRoute adminOnly>
            <SystemAnalyticsPage />
          </ExamProtectedRoute>
        }
      />
      <Route
        path="settings"
        element={
          <ExamProtectedRoute adminOnly>
            <ExamSettingsPage />
          </ExamProtectedRoute>
        }
      />

      {/* Faculty dashboard — accessible to all authenticated roles */}
      <Route
        path="faculty"
        element={
          <ExamProtectedRoute>
            <FacultyDashboardPage />
          </ExamProtectedRoute>
        }
      />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="dashboard" replace />} />
    </Routes>
  );
}
